<template>
	<transition name="full-image-fade">
		<div
			class="full-image"
			v-if="show"
			role="dialog"
			aria-modal="true"
			@click.self="close"
			@keydown.esc="close"
			tabindex="-1"
		>
			<div class="mask" @click="close"></div>
			<div class="preview-container" @click.stop>
				<div class="preview-frame">
					<div class="preview-toolbar">
						<div class="preview-meta">
							<div class="preview-title" :title="previewTitle">{{ previewTitle }}</div>
						</div>
						<div class="preview-toolbar-side">
							<div v-if="showStatusChip" class="image-status-chip" :class="statusChipClass">
								<span>{{ statusChipText }}</span>
							</div>
							<div class="toolbar-actions">
								<button
									type="button"
									class="toolbar-btn"
									:disabled="!source || isDownloading"
									:title="isDownloading ? '下载中' : '下载'"
									@click="downloadCurrent"
								>
									<i :class="isDownloading ? 'el-icon-loading' : 'el-icon-download'"></i>
								</button>
								<button class="toolbar-btn" type="button" @click="close" title="关闭">
									<i class="el-icon-close"></i>
								</button>
							</div>
						</div>
					</div>
					<div class="image-stage">
						<div class="image-shell" :class="{ 'is-loading': isLoading, 'has-error': hasError }">
							<div v-if="isLoading || hasError" class="viewer-placeholder">
								<div class="viewer-status-card">
									<div class="viewer-status-icon">
										<i :class="placeholderIcon"></i>
									</div>
									<div class="viewer-status-title">{{ placeholderTitle }}</div>
									<div class="viewer-status-text">{{ placeholderText }}</div>
									<button
										v-if="hasError"
										type="button"
										class="viewer-status-action"
										:disabled="isDownloading"
										@click="retryPreview"
									>
										{{ placeholderActionText }}
									</button>
								</div>
							</div>
							<img
								v-if="url"
								ref="image"
								:key="requestId"
								:data-request-id="requestId"
								:src="url"
								alt="preview-image"
								:class="{ ready: !isLoading && !hasError }"
								@load="onImageLoad"
								@error="onImageError"
							/>
						</div>
					</div>
				</div>
			</div>
		</div>
	</transition>
</template>

<script>
export default {
	name: "fullImage",
	data() {
		return {
			show: false,
			source: '',
			strict: false,
			currentFileName: '',
			currentContentType: '',
			currentKind: 'image',
			url: '',
			bodyOverflow: '',
			isLoading: false,
			hasError: false,
			errorText: '加载失败',
			isDownloading: false,
			requestId: 0,
			currentPayload: null
		}
	},
	mounted() {
		document.addEventListener('keydown', this.handleKeydown);
	},
	beforeDestroy() {
		document.removeEventListener('keydown', this.handleKeydown);
		this.restoreBodyOverflow();
	},
	methods: {
		async open(source) {
			const imagePayload = typeof source === 'string' ? { url: source } : (source || {});
			const currentRequestId = ++this.requestId;
			this.currentPayload = { ...imagePayload };
			this.bodyOverflow = document.body.style.overflow;
			this.show = true;
			this.source = imagePayload.url || '';
			this.strict = Boolean(imagePayload.strict);
			this.currentFileName = imagePayload.fileName || '';
			this.currentContentType = imagePayload.contentType || '';
			this.currentKind = imagePayload.kind || 'image';
			this.url = '';
			this.isLoading = true;
			this.hasError = false;
			this.errorText = '加载失败';
			this.isDownloading = false;
			document.body.style.overflow = 'hidden';
			this.$nextTick(() => {
				if (this.$el && this.$el.focus) {
					this.$el.focus();
				}
			});
			if (!this.source) {
				this.handlePreviewError('图片暂不可用');
				return;
			}
			if (this.strict && !this.$assetResolver.hasAssetSource(this.source)) {
				this.handlePreviewError('图片暂不可用');
				return;
			}
			try {
				const resolved = await this.$assetResolver.resolve(this.source, {
					fileName: this.currentFileName,
					contentType: this.currentContentType,
					kind: this.currentKind
				});
				if (currentRequestId !== this.requestId) {
					return;
				}
				if (!resolved.url) {
					this.handlePreviewError('图片暂不可用');
					return;
				}
				this.currentFileName = resolved.fileName || this.currentFileName;
				this.currentContentType = resolved.contentType || this.currentContentType;
				this.url = resolved.url;
			} catch (error) {
				if (currentRequestId !== this.requestId) {
					return;
				}
				this.handlePreviewError(error && error.message ? error.message : '图片加载失败');
			}
		},
		close() {
			this.requestId += 1;
			this.show = false;
			this.source = '';
			this.strict = false;
			this.currentFileName = '';
			this.currentContentType = '';
			this.currentKind = 'image';
			this.url = '';
			this.isLoading = false;
			this.hasError = false;
			this.errorText = '加载失败';
			this.isDownloading = false;
			this.currentPayload = null;
			this.restoreBodyOverflow();
		},
		retryPreview() {
			if (!this.currentPayload) {
				return;
			}
			this.open({ ...this.currentPayload });
		},
		isCurrentMediaEvent(event) {
			const target = event && event.target;
			return this.show && !!target && target === this.$refs.image &&
				String(this.requestId) === target.dataset.requestId && target.getAttribute('src') === this.url;
		},
		onImageLoad(event) {
			if (!this.isCurrentMediaEvent(event)) return;
			this.isLoading = false;
			this.hasError = false;
		},
		onImageError(event) {
			if (!this.isCurrentMediaEvent(event)) return;
			this.handlePreviewError('图片加载失败');
		},
		handlePreviewError(message) {
			this.isLoading = false;
			this.hasError = true;
			this.errorText = message || '图片加载失败';
		},
		handleKeydown(event) {
			if (event.key === 'Escape' && this.show) {
				this.close();
			}
		},
		resolvePreviewName() {
			if (this.currentFileName) {
				return this.currentFileName;
			}
			if (!this.source) {
				return '当前图片';
			}
			if (String(this.source).startsWith('data:')) {
				return '当前图片';
			}
			const normalized = String(this.source).split('?')[0].split('#')[0];
			const idx = normalized.lastIndexOf('/');
			if (idx < 0) {
				return normalized || '当前图片';
			}
			return normalized.slice(idx + 1) || '当前图片';
		},
		async downloadCurrent() {
			if (!this.source || this.isDownloading) {
				return;
			}
			this.isDownloading = true;
			try {
				await this.$assetResolver.download(this.source, {
					fileName: this.previewTitle,
					contentType: this.currentContentType,
					kind: this.currentKind
				});
				this.$message.success('图片下载已开始');
			} catch (error) {
				this.$message.error(error && error.message ? error.message : '图片下载失败');
			} finally {
				this.isDownloading = false;
			}
		},
		restoreBodyOverflow() {
			document.body.style.overflow = this.bodyOverflow || '';
		}
	},
	computed: {
		previewTitle() {
			return this.resolvePreviewName();
		},
		placeholderTitle() {
			if (this.isLoading) {
				return '正在加载图片';
			}
			return '图片暂时无法打开';
		},
		placeholderText() {
			if (this.isLoading) {
				return '图片资源准备完成后会自动显示。';
			}
			return this.errorText || '图片加载失败，请稍后重试。';
		},
		placeholderIcon() {
			return this.isLoading ? 'el-icon-loading' : 'el-icon-picture-outline';
		},
		placeholderActionText() {
			return this.isDownloading ? '处理中' : '重新加载';
		},
		showStatusChip() {
			return this.isDownloading || this.isLoading || this.hasError;
		},
		statusChipText() {
			if (this.isDownloading) {
				return '下载中';
			}
			if (this.isLoading) {
				return '加载中';
			}
			if (this.hasError) {
				return '加载失败';
			}
			return '';
		},
		statusChipClass() {
			if (this.isDownloading || this.isLoading) {
				return 'is-loading';
			}
			if (this.hasError) {
				return 'is-error';
			}
			return 'is-ready';
		}
	}
}
</script>

<style lang="scss">
.full-image {
	--preview-max-width: min(1240px, calc(100vw - 24px));
	--preview-max-height: min(92vh, calc(100vh - 24px));
	--preview-shell-padding: clamp(8px, 1.1vw, 12px);
	--preview-frame-radius: 16px;
	--preview-overlay-base: rgba(var(--im-black-rgb), 0.56);
	--preview-frame-bg: var(--im-surface-panel-strong);
	--preview-frame-border: var(--im-surface-panel-border);
	--preview-stage-bg: var(--im-background-secondary);
	--preview-shell-bg: var(--im-chat-bg);
	--preview-shadow:
		0 12px 32px -18px rgba(var(--im-black-rgb), 0.18);
	position: fixed;
	inset: 0;
	z-index: 9999;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 12px;
	box-sizing: border-box;

	.mask {
		position: absolute;
		inset: 0;
		background: var(--preview-overlay-base);
	}

	.preview-container {
		position: relative;
		width: var(--preview-max-width);
		z-index: 1;
	}

	.preview-frame {
		position: relative;
		display: flex;
		flex-direction: column;
		width: 100%;
		max-height: var(--preview-max-height);
		border-radius: var(--preview-frame-radius);
		overflow: hidden;
		background: var(--preview-frame-bg);
		border: 1px solid var(--preview-frame-border);
		box-shadow: var(--preview-shadow);
	}

	.preview-toolbar {
		display: flex;
		align-items: center;
		justify-content: space-between;
		gap: 10px;
		padding: 12px 14px;
		background: var(--preview-frame-bg);
		border-bottom: 1px solid var(--preview-frame-border);
	}

	.preview-meta {
		min-width: 0;
		flex: 1;
	}

	.preview-title {
		font-size: clamp(14px, 1.1vw, 17px);
		font-weight: 600;
		line-height: 1.3;
		color: var(--im-text-color-primary);
		white-space: nowrap;
		overflow: hidden;
		text-overflow: ellipsis;
	}

	.preview-toolbar-side {
		display: inline-flex;
		align-items: center;
		gap: 8px;
		flex-shrink: 0;
		min-width: 0;
	}

	.image-status-chip {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		gap: 6px;
		flex-shrink: 0;
		height: 28px;
		padding: 0 10px;
		border-radius: 999px;
		font-size: 11px;
		font-weight: 600;
		background: var(--im-control-bg);
		border: 1px solid var(--im-control-border);
		color: var(--im-text-color-light);

		&.is-loading {
			background: rgba(var(--im-primary-rgb), 0.06);
			border-color: rgba(var(--im-primary-rgb), 0.18);
			color: var(--im-color-primary);
		}

		&.is-error {
			background: rgba(var(--im-danger-rgb), 0.06);
			border-color: rgba(var(--im-danger-rgb), 0.18);
			color: var(--im-color-danger);
		}

		&.is-ready {
			background: rgba(var(--im-success-rgb, 103, 194, 58), 0.1);
			color: var(--im-color-success, #67c23a);
		}
	}

	.toolbar-actions {
		display: inline-flex;
		align-items: center;
		gap: 6px;
		flex-shrink: 0;
	}

	.toolbar-btn {
		display: inline-flex;
		align-items: center;
		justify-content: center;
		width: 36px;
		height: 36px;
		padding: 0;
		border: 1px solid var(--im-control-border);
		border-radius: 10px;
		background: var(--im-control-bg);
		color: var(--im-icon-color-strong);
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

		&:hover:not(:disabled) {
			background: var(--im-icon-hover-bg);
			border-color: var(--im-control-border-strong);
			color: var(--im-icon-hover-color);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.56;
		}

		i {
			font-size: 16px;
		}
	}

	.image-stage {
		min-height: 0;
		display: flex;
		align-items: center;
		justify-content: center;
		padding: var(--preview-shell-padding);
		background: var(--preview-stage-bg);
	}

	.image-shell {
		position: relative;
		width: auto;
		height: auto;
		min-width: min(260px, calc(100vw - 40px));
		min-height: min(180px, calc(100vh - 220px));
		max-width: calc(var(--preview-max-width) - (var(--preview-shell-padding) * 2));
		max-height: calc(var(--preview-max-height) - 88px);
		padding: 8px;
		border-radius: calc(var(--preview-frame-radius) - 2px);
		background: var(--preview-shell-bg);
		border: 1px solid var(--preview-frame-border);
		display: inline-flex;
		align-items: center;
		justify-content: center;
		overflow: hidden;
		box-sizing: border-box;

		&.is-loading,
		&.has-error {
			min-width: min(360px, calc(100vw - 40px));
			min-height: min(260px, calc(100vh - 180px));
		}

		img {
			display: block;
			width: auto;
			height: auto;
			max-width: calc(var(--preview-max-width) - (var(--preview-shell-padding) * 2) - 24px);
			max-height: calc(var(--preview-max-height) - 112px);
			object-fit: contain;
			border-radius: 10px;
			background: var(--im-chat-bg);
			border: 1px solid rgba(var(--im-black-rgb), 0.04);
			opacity: 0;
			transition: opacity 0.18s ease;
		}

		img.ready {
			opacity: 1;
		}

		.viewer-placeholder {
			position: absolute;
			inset: 0;
			display: flex;
			align-items: center;
			justify-content: center;
			padding: 20px;
			background: rgba(var(--im-background-rgb), 0.56);
			z-index: 2;
		}
	}

	.viewer-status-card {
		width: min(320px, 100%);
		padding: 18px 16px;
		border-radius: 12px;
		border: 1px solid var(--im-control-border);
		background: var(--im-surface-panel);
		text-align: center;
	}

	.viewer-status-icon {
		width: 36px;
		height: 36px;
		margin: 0 auto 8px;
		border-radius: 999px;
		display: inline-flex;
		align-items: center;
		justify-content: center;
		background: rgba(var(--im-primary-rgb), 0.1);
		color: var(--im-color-primary);

		i {
			font-size: 16px;
		}
	}

	.viewer-status-title {
		font-size: 15px;
		font-weight: 600;
		line-height: 1.4;
		color: var(--im-text-color-primary);
	}

	.viewer-status-text {
		margin-top: 6px;
		font-size: 12px;
		line-height: 1.6;
		color: var(--im-text-color-light);
	}

	.viewer-status-action {
		min-width: 96px;
		height: 34px;
		margin-top: 14px;
		padding: 0 16px;
		border: 1px solid rgba(var(--im-primary-rgb), 0.18);
		border-radius: 10px;
		background: transparent;
		color: var(--im-color-primary);
		font-size: 12px;
		font-weight: 600;
		cursor: pointer;
		transition: background-color 0.2s ease, border-color 0.2s ease, color 0.2s ease;

		&:hover:not(:disabled) {
			background: rgba(var(--im-primary-rgb), 0.12);
			border-color: rgba(var(--im-primary-rgb), 0.24);
		}

		&:disabled {
			cursor: not-allowed;
			opacity: 0.56;
		}
	}

	.image-shell.has-error {
		.viewer-status-icon {
			background: rgba(var(--im-danger-rgb), 0.1);
			color: var(--im-color-danger);
		}
	}
}

[data-theme="dark"] .full-image {
	--preview-overlay-base: rgba(var(--im-black-rgb), 0.72);
	--preview-frame-bg: var(--im-surface-panel-strong);
	--preview-frame-border: rgba(var(--im-white-rgb), 0.08);
	--preview-stage-bg: var(--im-background-secondary);
	--preview-shell-bg: var(--im-chat-bg-alt);
	--preview-shadow: 0 16px 40px -22px rgba(var(--im-black-rgb), 0.46);
}

.full-image-fade-enter-active,
.full-image-fade-leave-active {
	transition: opacity 0.24s ease;
}

.full-image-fade-enter-active .preview-frame,
.full-image-fade-leave-active .preview-frame {
	transition: transform 0.28s cubic-bezier(0.22, 1, 0.36, 1), opacity 0.24s ease;
}

.full-image-fade-enter,
.full-image-fade-leave-to {
	opacity: 0;
}

.full-image-fade-enter .preview-frame,
.full-image-fade-leave-to .preview-frame {
	opacity: 0;
	transform: translateY(12px) scale(0.985);
}

@media (max-width: 640px) {
	.full-image {
		--preview-max-width: calc(100vw - 12px);
		--preview-max-height: calc(100vh - 12px);
		--preview-shell-padding: 8px;
		--preview-frame-radius: 14px;
		padding: 6px;

		.preview-container {
			width: var(--preview-max-width);
		}

		.preview-toolbar {
			padding: 10px 10px 8px;
			align-items: flex-start;
			flex-wrap: wrap;
			gap: 8px;
		}

		.preview-meta {
			width: 100%;
		}

		.preview-title {
			font-size: 14px;
		}

		.preview-toolbar-side {
			width: 100%;
			justify-content: space-between;
		}

		.toolbar-btn {
			width: 32px;
			height: 32px;
			border-radius: 9px;

			i {
				font-size: 14px;
			}
		}

		.toolbar-actions {
			gap: 6px;
		}

		.image-shell {
			width: 100%;
			min-width: 0;
			min-height: min(200px, calc(100vh - 152px));
			max-height: calc(var(--preview-max-height) - 72px);
			padding: 8px;
			border-radius: 12px;

			&.is-loading,
			&.has-error {
				min-width: 0;
				min-height: min(220px, calc(100vh - 140px));
			}

			img {
				max-width: 100%;
				max-height: calc(var(--preview-max-height) - 98px);
				border-radius: 10px;
			}
		}

		.viewer-status-card {
			padding: 18px 16px;
			border-radius: 14px;
		}

		.viewer-status-title {
			font-size: 14px;
		}

		.viewer-status-text {
			font-size: 11px;
		}
	}
}
</style>
